import { useState } from 'react';
import { AdminOrder, AdminOrderStatus } from '../../../mocks/adminOrders';
import DeliveryMap from '../../../../components/feature/DeliveryMap';
import { formatEtaRange } from '../../../../utils/routeUtils';

interface OrderDetailModalProps {
  order: AdminOrder;
  onClose: () => void;
  onStatusChange: (id: string, status: AdminOrderStatus) => void;
}

const statusOptions: { value: AdminOrderStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'on_way', label: 'On the Way' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const statusConfig: Record<AdminOrderStatus, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  processing: { label: 'Processing', className: 'bg-sky-50 text-sky-700 border-sky-200' },
  on_way: { label: 'On the Way', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  delivered: { label: 'Delivered', className: 'bg-green-50 text-green-700 border-green-200' },
  cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-700 border-red-200' },
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function OrderDetailModal({ order, onClose, onStatusChange }: OrderDetailModalProps) {
  const cfg = statusConfig[order.status] ?? statusConfig['pending'];
  const [activeTab, setActiveTab] = useState<'details' | 'map'>('details');
  const etaRange = formatEtaRange(order.address);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl border border-stone-200 w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <div>
            <p className="text-xs text-stone-400 font-medium">Order</p>
            <p className="text-lg font-bold text-stone-900 leading-tight">{order.id}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Tab toggle */}
            <div className="flex items-center gap-0.5 bg-stone-100 rounded-lg p-0.5">
              <button
                onClick={() => setActiveTab('details')}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition cursor-pointer whitespace-nowrap ${activeTab === 'details' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400 hover:text-stone-600'}`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('map')}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-1 ${activeTab === 'map' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400 hover:text-stone-600'}`}
              >
                <i className="ri-map-2-line" />
                Route
              </button>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 cursor-pointer transition">
              <i className="ri-close-line text-stone-500" />
            </button>
          </div>
        </div>

        {activeTab === 'details' ? (
          <div className="px-6 py-5 space-y-5">
            {/* Customer */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-stone-400 font-medium mb-1">Customer</p>
                <p className="text-sm font-semibold text-stone-800">{order.customer}</p>
                <p className="text-xs text-stone-500">{order.phone}</p>
              </div>
              <div>
                <p className="text-xs text-stone-400 font-medium mb-1">Delivery Address</p>
                <p className="text-sm text-stone-700">{order.address}</p>
                <p className="text-xs text-orange-500 font-semibold mt-0.5">ETA: {etaRange}</p>
              </div>
            </div>

            {/* Items */}
            <div>
              <p className="text-xs text-stone-400 font-medium mb-2">Items</p>
              <div className="space-y-1.5">
                {order.items.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <span className="text-stone-700">{item.name} <span className="text-stone-400">×{item.qty}</span></span>
                    <span className="font-medium text-stone-800">GYD ${item.price.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-stone-100 mt-2 pt-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-stone-700">Total</span>
                <span className="text-base font-bold text-orange-600">GYD ${order.total.toLocaleString()}</span>
              </div>
            </div>

            {/* Meta */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-stone-400 font-medium mb-1">Payment</p>
                <p className="text-sm text-stone-700">{order.paymentMethod}</p>
              </div>
              <div>
                <p className="text-xs text-stone-400 font-medium mb-1">Placed at</p>
                <p className="text-sm text-stone-700">{formatDate(order.createdAt)}</p>
              </div>
            </div>

            {/* Change info — only for Cash on Delivery */}
            {order.paymentMethod === 'Cash on Delivery' && order.changeAmount !== undefined && (
              <div className={`rounded-xl px-4 py-3 border ${order.changeAmount >= 0 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
                <p className="text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5 ${order.changeAmount >= 0 ? 'text-amber-700' : 'text-red-700'}">
                  <i className="ri-coins-line" />
                  Troco / Change for Rider
                </p>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-stone-500">Customer pays:</span>
                      <span className="font-semibold text-stone-800">GYD ${(order.cashAmount ?? 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-stone-500">Order total:</span>
                      <span className="font-semibold text-stone-800">GYD ${order.total.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className={`text-right ${order.changeAmount >= 0 ? 'text-amber-700' : 'text-red-600'}`}>
                    <p className="text-xs font-medium mb-0.5">
                      {order.changeAmount >= 0 ? 'Bring change of' : 'Insufficient cash'}
                    </p>
                    <p className="text-xl font-black">
                      GYD ${Math.abs(order.changeAmount).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {order.rider && (
              <div>
                <p className="text-xs text-stone-400 font-medium mb-1">Rider</p>
                <p className="text-sm text-stone-700">{order.rider}</p>
              </div>
            )}

            {order.note && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                <p className="text-xs text-amber-700 font-medium mb-0.5">Note</p>
                <p className="text-sm text-amber-800">{order.note}</p>
              </div>
            )}

            {/* Status Update */}
            <div>
              <p className="text-xs text-stone-400 font-medium mb-2">Update Status</p>
              <div className="flex flex-wrap gap-2">
                {statusOptions.map((opt) => {
                  const active = order.status === opt.value;
                  const c = statusConfig[opt.value];
                  return (
                    <button
                      key={opt.value}
                      onClick={() => onStatusChange(order.id, opt.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition cursor-pointer whitespace-nowrap ${
                        active
                          ? c.className + ' ring-2 ring-offset-1 ring-current'
                          : 'bg-stone-50 text-stone-500 border-stone-200 hover:bg-stone-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-stone-400">Current status:</span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.className}`}>
                {cfg.label}
              </span>
            </div>
          </div>
        ) : (
          /* ── Map Tab ── */
          <div className="p-4">
            <DeliveryMap destination={order.address} />
          </div>
        )}
      </div>
    </div>
  );
}
